/**
 * Copied from three.js https://raw.githubusercontent.com/semleti/three.ts/refs/heads/master/src/animation/KeyframeTrack.ts
 * Applied following changes:
 * - Fixed import paths
 * - Fixed a bunch of TS errors
 * - Export instead of module.exports
 * - Changing Float32Array to Float64Array for better precision
 */
import {
  AnimationUtils,
  BooleanKeyframeTrack,
  ColorKeyframeTrack,
  CubicInterpolant,
  DiscreteInterpolant,
  Interpolant,
  InterpolateDiscrete,
  InterpolateLinear,
  InterpolateSmooth,
  LinearInterpolant,
  NumberKeyframeTrack,
  QuaternionKeyframeTrack,
  StringKeyframeTrack,
  VectorKeyframeTrack,
} from 'three';

/**
 * A timed sequence of keyframes for a specific property.
 */
export class KeyframeTrack {
  name: string;
  times: Float64Array;
  values: Float64Array;

  createInterpolant: (result: any) => Interpolant = () =>
    new LinearInterpolant(new Float64Array(), new Float64Array(), 0);

  constructor(
    name: string,
    times: Array<number> | Float64Array | number,
    values: Array<any>,
    interpolation?: number,
  ) {
    if (name === undefined)
      throw new Error('THREE.KeyframeTrack: track name is undefined');
    if (
      times === undefined ||
      (times as Array<number> | Float64Array).length === 0
    )
      throw new Error(
        'THREE.KeyframeTrack: no keyframes in track named ' + name,
      );

    this.name = name;

    this.times = AnimationUtils.convertArray(times, this.TimeBufferType, false);
    this.values = AnimationUtils.convertArray(
      values,
      this.ValueBufferType,
      false,
    );

    this.setInterpolation(interpolation || this.DefaultInterpolation);

    this.validate();
    this.optimize();
  }

  static parse(json: KeyframeTrackData): KeyframeTrack {
    if (json.type === undefined) {
      throw new Error(
        'THREE.KeyframeTrack: track type undefined, can not parse',
      );
    }

    const trackType = KeyframeTrack._getTrackTypeForValueTypeName(json.type);

    if (json.times === undefined) {
      const times: number[] = [],
        values: any[] = [];

      AnimationUtils.flattenJSON(json.keys, times, values, 'value');

      json.times = times;
      json.values = values;
    }

    if (trackType.parse !== undefined) {
      return trackType.parse(json);
    } else {
      return new trackType(
        json.name,
        json.times,
        json.values,
        json.interpolation,
      );
    }
  }

  static toJSON(track: KeyframeTrack): KeyframeTrackData {
    const trackType = track;

    let json = new KeyframeTrackData();

    if ((trackType as any).toJSON !== undefined) {
      json = (trackType as any).toJSON(track);
    } else {
      json.name = track.name;
      json.times = AnimationUtils.convertArray(track.times, Array, false);
      json.values = AnimationUtils.convertArray(track.values, Array, false);

      const interpolation = track.getInterpolation();

      if (interpolation !== track.DefaultInterpolation) {
        if (interpolation !== undefined) {
          json.interpolation = interpolation;
        }
      }
    }

    json.type = track.ValueTypeName;

    return json;
  }

  static _getTrackTypeForValueTypeName(typeName: string): typeof KeyframeTrack {
    switch (typeName.toLowerCase()) {
      case 'scalar':
      case 'double':
      case 'float':
      case 'number':
      case 'integer':
        return NumberKeyframeTrack as unknown as typeof KeyframeTrack;

      case 'vector':
      case 'vector2':
      case 'vector3':
      case 'vector4':
        return VectorKeyframeTrack as unknown as typeof KeyframeTrack;

      case 'color':
        return ColorKeyframeTrack as unknown as typeof KeyframeTrack;

      case 'quaternion':
        return QuaternionKeyframeTrack as unknown as typeof KeyframeTrack;

      case 'bool':
      case 'boolean':
        return BooleanKeyframeTrack as unknown as typeof KeyframeTrack;

      case 'string':
        return StringKeyframeTrack as any;
    }

    throw new Error('THREE.KeyframeTrack: Unsupported typeName: ' + typeName);
  }

  TimeBufferType = Float64Array;

  ValueBufferType = Float64Array;

  DefaultInterpolation: number = InterpolateLinear;

  InterpolantFactoryMethodDiscrete(result: any): DiscreteInterpolant {
    return new DiscreteInterpolant(
      this.times,
      this.values,
      this.getValueSize(),
      result,
    );
  }

  InterpolantFactoryMethodLinear(result: any): LinearInterpolant {
    return new LinearInterpolant(
      this.times,
      this.values,
      this.getValueSize(),
      result,
    );
  }

  InterpolantFactoryMethodSmooth(result: any): CubicInterpolant {
    return new CubicInterpolant(
      this.times,
      this.values,
      this.getValueSize(),
      result,
    );
  }

  ValueTypeName = '';

  setInterpolation(interpolation: number): number {
    let factoryMethod;

    switch (interpolation) {
      case InterpolateDiscrete:
        factoryMethod = this.InterpolantFactoryMethodDiscrete;
        break;

      case InterpolateLinear:
        factoryMethod = this.InterpolantFactoryMethodLinear;
        break;

      case InterpolateSmooth:
        factoryMethod = this.InterpolantFactoryMethodSmooth;
        break;
    }

    if (factoryMethod === undefined) {
      const message =
        'unsupported interpolation for ' +
        this.ValueTypeName +
        ' keyframe track named ' +
        this.name;

      if (this.createInterpolant === undefined) {
        if (interpolation !== this.DefaultInterpolation) {
          this.setInterpolation(this.DefaultInterpolation);
        } else {
          throw new Error(message);
        }
      }

      console.warn('THREE.KeyframeTrack:', message);
      return interpolation;
    }

    this.createInterpolant = factoryMethod;
    return interpolation;
  }

  getInterpolation(): number | undefined {
    switch (this.createInterpolant) {
      case this.InterpolantFactoryMethodDiscrete:
        return InterpolateDiscrete;

      case this.InterpolantFactoryMethodLinear:
        return InterpolateLinear;

      case this.InterpolantFactoryMethodSmooth:
        return InterpolateSmooth;
    }
    return undefined;
  }

  getValueSize(): number {
    return this.values.length / this.times.length;
  }

  shift(timeOffset: number): KeyframeTrack {
    if (timeOffset !== 0.0) {
      const times = this.times;

      for (let i = 0, n = times.length; i !== n; ++i) {
        times[i] += timeOffset;
      }
    }

    return this;
  }

  scale(timeScale: number): KeyframeTrack {
    if (timeScale !== 1.0) {
      const times = this.times;

      for (let i = 0, n = times.length; i !== n; ++i) {
        times[i] *= timeScale;
      }
    }

    return this;
  }

  trim(startTime: number, endTime: number): KeyframeTrack {
    const times = this.times,
      nKeys = times.length;
    let from = 0,
      to = nKeys - 1;

    while (from !== nKeys && times[from] < startTime) {
      ++from;
    }

    while (to !== -1 && times[to] > endTime) {
      --to;
    }

    ++to;

    if (from !== 0 || to !== nKeys) {
      if (from >= to) (to = Math.max(to, 1)), (from = to - 1);

      const stride = this.getValueSize();
      this.times = times.slice(from, to);
      this.values = this.values.slice(from * stride, to * stride);
    }

    return this;
  }

  validate(): boolean {
    let valid = true;

    const valueSize = this.getValueSize();
    if (valueSize - Math.floor(valueSize) !== 0) {
      console.error('THREE.KeyframeTrack: Invalid value size in track.', this);
      valid = false;
    }

    const times = this.times,
      values = this.values,
      nKeys = times.length;

    if (nKeys === 0) {
      console.error('THREE.KeyframeTrack: Track is empty.', this);
      valid = false;
    }

    let prevTime = null;

    for (let i = 0; i !== nKeys; i++) {
      const currTime = times[i];

      if (typeof currTime === 'number' && isNaN(currTime)) {
        console.error(
          'THREE.KeyframeTrack: Time is not a valid number.',
          this,
          i,
          currTime,
        );
        valid = false;
        break;
      }

      if (prevTime !== null && prevTime > currTime) {
        console.error(
          'THREE.KeyframeTrack: Out of order keys.',
          this,
          i,
          currTime,
          prevTime,
        );
        valid = false;
        break;
      }

      prevTime = currTime;
    }

    if (values !== undefined) {
      if (AnimationUtils.isTypedArray(values)) {
        for (let i = 0, n = values.length; i !== n; ++i) {
          const value = values[i];

          if (isNaN(value)) {
            console.error(
              'THREE.KeyframeTrack: Value is not a valid number.',
              this,
              i,
              value,
            );
            valid = false;
            break;
          }
        }
      }
    }

    return valid;
  }

  optimize(): KeyframeTrack {
    const times = this.times,
      values = this.values,
      stride = this.getValueSize(),
      smoothInterpolation = this.getInterpolation() === InterpolateSmooth,
      lastIndex = times.length - 1;
    let writeIndex = 1;

    for (let i = 1; i < lastIndex; ++i) {
      let keep = false;

      const time = times[i];
      const timeNext = times[i + 1];

      if (time !== timeNext && (i !== 1 || time !== times[0])) {
        if (!smoothInterpolation) {
          const offset = i * stride,
            offsetP = offset - stride,
            offsetN = offset + stride;

          for (let j = 0; j !== stride; ++j) {
            const value = values[offset + j];

            if (
              value !== values[offsetP + j] ||
              value !== values[offsetN + j]
            ) {
              keep = true;
              break;
            }
          }
        } else {
          keep = true;
        }
      }

      if (keep) {
        if (i !== writeIndex) {
          times[writeIndex] = times[i];

          const readOffset = i * stride,
            writeOffset = writeIndex * stride;

          for (let j = 0; j !== stride; ++j) {
            values[writeOffset + j] = values[readOffset + j];
          }
        }

        ++writeIndex;
      }
    }

    if (lastIndex > 0) {
      times[writeIndex] = times[lastIndex];

      for (
        let readOffset = lastIndex * stride,
          writeOffset = writeIndex * stride,
          j = 0;
        j !== stride;
        ++j
      ) {
        values[writeOffset + j] = values[readOffset + j];
      }

      ++writeIndex;
    }

    if (writeIndex !== times.length) {
      this.times = times.slice(0, writeIndex);
      this.values = values.slice(0, writeIndex * stride);
    }

    return this;
  }

  clone(): KeyframeTrack {
    const times = this.times.slice();
    const values = this.values.slice();

    const TypedKeyframeTrack = this.constructor as any;
    const track = new TypedKeyframeTrack(this.name, times, values);

    track.createInterpolant = this.createInterpolant;

    return track;
  }
}

export class KeyframeTrackData {
  type = '';
  times: Array<number> = [];
  values: Array<unknown> = [];
  keys: Array<string> = [];
  name = '';
  interpolation = 0;
}
